import { useEffect, useState } from 'react';
import { getFAQItems } from '@/mocks/faq';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { OrganizationLD, WebPageLD, FAQPageLD } from '@/components/feature/JsonLD';

export default function AboutPage() {
  const [faqOpenId, setFaqOpenId] = useState<string | null>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const formUrl = 'https://readdy.ai/api/form/d9s9chue3oq4jqumu7h0';

  useEffect(() => {
    document.title = '关于萌白酱 - 萌白酱Official';
  }, []);

  const faqs = getFAQItems();

  const toggleFaq = (id: string) => {
    setFaqOpenId(faqOpenId === id ? null : id);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt') as string || '';

    if (honeypot.trim()) {
      setFormSubmitted(true);
      form.reset();
      setFormLoading(false);
      return;
    }

    formData.delete('website_alt');

    try {
      const res = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(serverMsg);
      }
    } catch {
      setFormError('网络错误，请稍后重试');
    } finally {
      setFormLoading(false);
    }
  };

  const milestones = [
    { year: '2020', title: '初入B站', desc: '萌白酱在B站发布首个视频，以清新可爱的形象进入观众视野，开启虚拟UP主创作之旅。' },
    { year: '2021', title: '快速成长', desc: '凭借高质量的cosplay作品和有趣的视频内容，粉丝数量快速增长，突破10万关注。' },
    { year: '2022', title: '破圈发展', desc: '与多位知名UP主合作联动，视频内容覆盖游戏、舞蹈、生活Vlog等多个领域，粉丝突破50万。' },
    { year: '2023', title: '品牌确立', desc: '正式确立萌白酱品牌形象，推出首批官方周边产品，开始参加全国各大漫展活动。' },
    { year: '2024', title: '多元拓展', desc: 'cosplay作品多次登上热门榜单，粉丝社群蓬勃发展，B站粉丝突破80万。' },
    { year: '2025-2026', title: '全新篇章', desc: 'B站粉丝突破100万，筹备个人原创音乐作品，持续为粉丝带来更多精彩内容。' },
  ];

  const testimonials = [
    { name: '小兔子', role: '资深粉丝', content: '从2020年就开始关注萌白酱了，见证了她的成长和进步。每一期视频都充满诚意，cosplay还原度越来越高。萌白酱是我最喜欢的UP主！', avatar: 'https://readdy.ai/api/search-image?query=Cute%20kawaii%20anime%20style%20girl%20avatar%20portrait%2C%20soft%20pink%20background%2C%20gentle%20smile%2C%20simple%20minimalist%20style%2C%20clean%20illustration&width=100&height=100&seq=mengbai-testimonial-1&orientation=squarish' },
    { name: '星空', role: '社群管理员', content: '作为萌白酱粉丝社群的管理员，我亲眼见证了社群的温暖氛围。萌白酱非常重视与粉丝的互动，每次活动都用心准备，真正把粉丝当朋友。', avatar: 'https://readdy.ai/api/search-image?query=Simple%20cute%20anime%20style%20character%20portrait%20avatar%2C%20warm%20color%20palette%2C%20friendly%20expression%2C%20minimalist%20design%2C%20clean%20background%2C%20illustration%20style&width=100&height=100&seq=mengbai-testimonial-2&orientation=squarish' },
    { name: '花火', role: 'cosplay爱好者', content: '萌白酱的cosplay作品给了我很多灵感。她对角色的理解和还原度真的太棒了，每次发布新作品我都会第一时间观看和学习。', avatar: 'https://readdy.ai/api/search-image?query=Cute%20anime%20style%20girl%20avatar%20with%20pink%20hair%2C%20soft%20pastel%20colors%2C%20simple%20portrait%2C%20kawaii%20style%2C%20clean%20illustration%2C%20minimalist&width=100&height=100&seq=mengbai-testimonial-3&orientation=squarish' },
  ];

  return (
    <>
      <OrganizationLD
        name="萌白酱Official"
        url="https://mengbaijiang.com"
        logo="https://mengbaijiang.com/logo.png"
        description="萌白酱是深受粉丝喜爱的知名虚拟UP主与cosplay创作者"
        sameAs={[
          'https://space.bilibili.com/萌白酱',
          'https://weibo.com/萌白酱',
          'https://www.douyin.com/user/萌白酱',
        ]}
      />
      <WebPageLD
        url="https://mengbaijiang.com/about"
        name="关于萌白酱 - 萌白酱Official"
        description="了解萌白酱的品牌故事、发展历程、粉丝口碑以及联系方式"
        breadcrumb={[
          { name: '首页', url: 'https://mengbaijiang.com' },
          { name: '关于萌白酱', url: 'https://mengbaijiang.com/about' },
        ]}
      />
      <FAQPageLD questions={faqs.map(f => ({ question: f.question, answer: f.answer }))} />

      <main className="pt-16">
        <section className="relative w-full h-[280px] md:h-[380px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20cozy%20creative%20studio%20workspace%20with%20soft%20pink%20lighting%2C%20cute%20decorations%2C%20inspirational%20wall%20with%20photos%20and%20notes%2C%20comfortable%20atmosphere%2C%20professional%20content%20creator%20environment%2C%20dreamy%20aesthetic%2C%20high%20quality%20photography&width=1920&height=600&seq=mengbai-about-hero&orientation=landscape"
            alt="关于萌白酱"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于萌白酱' },
              ]}
            />
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-white mt-2">
              关于萌白酱
            </h1>
            <p className="text-sm md:text-base text-white/70 mt-1 max-w-xl">
              了解萌白酱的创作故事、品牌历程与独特魅力
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4">品牌介绍</h2>
              <div className="w-16 h-1 bg-primary-500 mx-auto rounded-full mb-6"></div>
              <p className="text-base text-foreground-600 leading-relaxed max-w-3xl mx-auto">
                萌白酱是活跃于B站等平台的知名虚拟UP主与cosplay创作者。以甜美可爱的形象、高质量的cosplay作品和丰富多样的视频内容，赢得了百万粉丝的喜爱与支持。萌白酱不仅是一位内容创作者，更是连接粉丝、传递快乐与正能量的文化符号。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
              {[
                { icon: 'ri-user-heart-line', title: '以粉丝为中心', desc: '始终把粉丝体验放在首位，用心创作每一份内容' },
                { icon: 'ri-star-line', title: '追求高品质', desc: '对每一部作品精益求精，从服装道具到后期制作追求极致' },
                { icon: 'ri-heart-pulse-line', title: '传递正能量', desc: '用快乐和美好感染每一位观众，营造温暖有爱的社区氛围' },
              ].map((item, index) => (
                <div key={index} className="bg-background-100 border border-background-200/70 rounded-2xl p-6 text-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600 mb-4">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-heading text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500">{item.desc}</p>
                </div>
              ))}
            </div>

            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 text-center mb-10">发展历程</h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((milestone, index) => (
                  <div key={index} className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="md:w-1/2 pl-10 md:pl-0 md:px-8">
                      <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5">
                        <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-2">
                          {milestone.year}
                        </span>
                        <h3 className="text-base font-heading text-foreground-900 mb-1">{milestone.title}</h3>
                        <p className="text-sm text-foreground-500">{milestone.desc}</p>
                      </div>
                    </div>
                    <div className="absolute left-3 md:left-1/2 top-5 md:top-6 w-3 h-3 bg-primary-500 rounded-full border-4 border-background-50 -translate-x-1/2"></div>
                    <div className="md:w-1/2 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4">粉丝口碑</h2>
              <div className="w-16 h-1 bg-primary-500 mx-auto rounded-full mb-6"></div>
              <p className="text-sm text-foreground-500">来自粉丝们的真实评价</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-background-50 border border-background-200/70 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-11 h-11 rounded-full object-cover"
                    />
                    <div>
                      <h4 className="text-sm font-heading text-foreground-900">{testimonial.name}</h4>
                      <span className="text-xs text-foreground-400">{testimonial.role}</span>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{testimonial.content}</p>
                  <div className="flex items-center gap-0.5 mt-3">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-xs"></i>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4">联系我们</h2>
              <div className="w-16 h-1 bg-primary-500 mx-auto rounded-full mb-6"></div>
              <p className="text-sm text-foreground-500">商务合作、媒体采访、粉丝投稿，欢迎联系我们</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-12">
              {[
                { icon: 'ri-mail-line', title: '商务合作', desc: 'mengbaijiang@example.com', color: 'bg-primary-100 text-primary-600' },
                { icon: 'ri-message-3-line', title: '粉丝投稿', desc: 'fan@example.com', color: 'bg-accent-100 text-accent-600' },
                { icon: 'ri-phone-line', title: '媒体联系', desc: 'media@example.com', color: 'bg-secondary-100 text-secondary-600' },
              ].map((item, index) => (
                <div key={index} className="bg-background-100 border border-background-200/70 rounded-2xl p-6 text-center">
                  <div className={`w-12 h-12 mx-auto flex items-center justify-center rounded-xl ${item.color} mb-3`}>
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h4 className="text-sm font-heading text-foreground-900 mb-1">{item.title}</h4>
                  <p className="text-xs text-foreground-500">{item.desc}</p>
                </div>
              ))}
            </div>

            {formSubmitted ? (
              <div className="bg-background-100 border border-green-200 rounded-2xl p-8 text-center">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-green-100 text-green-600 mb-4">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-heading text-foreground-900 mb-2">消息已发送！</h3>
                <p className="text-sm text-foreground-500">感谢您的留言，我们将在3个工作日内回复。</p>
                <button
                  onClick={() => setFormSubmitted(false)}
                  className="mt-6 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
                >
                  发送新消息
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-background-100 border border-background-200/70 rounded-2xl p-6 md:p-8" data-readdy-form>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-1.5">姓名</label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">主题</label>
                  <input
                    type="text"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors duration-200"
                    placeholder="请输入消息主题"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">留言内容</label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors duration-200 resize-none"
                    placeholder="请输入您的留言内容（500字以内）"
                  ></textarea>
                </div>

                <div className="honeypot-field">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>

                {formError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formLoading}
                  className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
                >
                  {formLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      发送中...
                    </span>
                  ) : (
                    <span>
                      <i className="ri-send-plane-line mr-2"></i>
                      发送消息
                    </span>
                  )}
                </button>
              </form>
            )}
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4">常见问题</h2>
              <div className="w-16 h-1 bg-primary-500 mx-auto rounded-full"></div>
            </div>
            <div className="space-y-3">
              {faqs.slice(0, 5).map((faq) => (
                <div key={faq.id} className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
                  <button
                    onClick={() => toggleFaq(faq.id)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-background-100/50 transition-colors duration-200"
                  >
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full transition-all duration-300 flex-shrink-0 ${
                      faqOpenId === faq.id ? 'bg-primary-500 text-background-50 rotate-180' : 'bg-background-200 text-foreground-500'
                    }`}>
                      <i className="ri-arrow-down-s-line text-sm"></i>
                    </div>
                  </button>
                  {faqOpenId === faq.id && (
                    <div className="px-5 pb-5 animate-fade-in">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}